import { useState } from 'react';

import { parseHex, toHex, type RGBA } from '../../lib/pixel-doc.js';
import { promptText } from '../../lib/prompt-dialog.js';
import { usePixel } from '../../state/pixel.js';
import { ContextMenu, IconButton, type MenuItem } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';

// Selectors must return stable values: a fresh `[]` on every call makes the store
// look changed on every render, which loops forever (React error 185) and froze
// the app whenever the Pixel tab opened with no sprite loaded.
const NO_COLORS: readonly string[] = [];
const NO_LAYERS: readonly never[] = [];

function css(c: RGBA): string {
  return `rgba(${c[0]},${c[1]},${c[2]},${c[3] / 255})`;
}

function ColorPanel() {
  const color = usePixel((s) => s.color);
  const secondary = usePixel((s) => s.secondary);
  const s = usePixel.getState();
  const [hex, setHex] = useState<string | null>(null);
  return (
    <section className="pixel-section">
      <div className="pixel-section-head">Color</div>
      <div className="pixel-colors">
        <div className="pixel-swatches">
          <label className="pixel-swatch big" style={{ background: css(color) }} title="Primary color (left click)">
            <input
              type="color"
              value={toHex([color[0], color[1], color[2], 255])}
              onChange={(e) => {
                const c = parseHex(e.target.value);
                if (c) s.set({ color: [c[0], c[1], c[2], color[3]] });
              }}
            />
          </label>
          <label className="pixel-swatch small" style={{ background: css(secondary) }} title="Secondary color (right click)">
            <input
              type="color"
              value={toHex([secondary[0], secondary[1], secondary[2], 255])}
              onChange={(e) => {
                const c = parseHex(e.target.value);
                if (c) s.set({ secondary: [c[0], c[1], c[2], secondary[3]] });
              }}
            />
          </label>
          <IconButton icon="refresh" title="Swap colors" shortcut="X" onClick={s.swapColors} size={13} />
        </div>
        <div className="pixel-color-fields">
          <input
            className="mono"
            value={hex ?? toHex(color)}
            onChange={(e) => setHex(e.target.value)}
            onBlur={() => {
              const c = hex ? parseHex(hex) : null;
              if (c) s.set({ color: c });
              setHex(null);
            }}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
            aria-label="Color hex"
          />
          <label className="pixel-alpha" title="Opacity">
            <span className="muted">α</span>
            <input
              type="range"
              min={0}
              max={255}
              value={color[3]}
              onChange={(e) => s.set({ color: [color[0], color[1], color[2], Number(e.target.value)] })}
            />
          </label>
        </div>
      </div>
    </section>
  );
}

function PalettePanel() {
  const palette = usePixel((s) => s.doc?.palette ?? NO_COLORS);
  const color = usePixel((s) => s.color);
  const [menu, setMenu] = useState<{ x: number; y: number; index: number } | null>(null);
  const s = usePixel.getState();
  const current = toHex(color);
  const items: MenuItem[] = menu
    ? [
        { id: 'set', label: 'Replace with current color (recolors art)', icon: 'edit', run: () => s.setPaletteColor(menu.index, current) },
        {
          id: 'edit',
          label: 'Edit hex…',
          icon: 'edit',
          run: () => {
            void promptText('Palette color (#rrggbb or #rrggbbaa)', palette[menu.index] ?? '').then((v) => {
              if (v && parseHex(v)) s.setPaletteColor(menu.index, v);
            });
          },
        },
        { id: 'del', label: 'Remove from palette', icon: 'trash', danger: true, run: () => s.removePaletteColor(menu.index) },
      ]
    : [];
  return (
    <section className="pixel-section">
      <div className="pixel-section-head">
        Palette <span className="muted">{palette.length}/62</span>
        <span className="bar-spacer" />
        <IconButton icon="plus" title="Add current color" size={13} onClick={() => s.addPaletteColor(current)} />
        <IconButton icon="wand" title="Palette from the colors used in the art" size={13} onClick={s.paletteFromArt} />
      </div>
      <div className="pixel-palette">
        {palette.map((c, i) => {
          const rgba = parseHex(c);
          if (!rgba) return null;
          const on = toHex(rgba) === current;
          return (
            <button
              key={`${c}-${i}`}
              type="button"
              className={`pixel-chip${on ? ' is-on' : ''}`}
              style={{ background: css(rgba) }}
              title={`${c} (right-click for options)`}
              onClick={() => s.set({ color: rgba })}
              onContextMenu={(e) => {
                e.preventDefault();
                setMenu({ x: e.clientX, y: e.clientY, index: i });
              }}
              onAuxClick={(e) => e.button === 1 && s.set({ secondary: rgba })}
            />
          );
        })}
      </div>
      {menu && <ContextMenu x={menu.x} y={menu.y} items={items} onClose={() => setMenu(null)} />}
    </section>
  );
}

function LayersPanel() {
  const layers = usePixel((s) => s.doc?.layers ?? NO_LAYERS);
  const active = usePixel((s) => s.layer);
  const s = usePixel.getState();
  const current = layers[active];
  return (
    <section className="pixel-section">
      <div className="pixel-section-head">
        Layers
        <span className="bar-spacer" />
        <IconButton icon="plus" title="Add layer" size={13} onClick={s.addLayer} />
        <IconButton icon="arrowUp" title="Move layer up" size={13} onClick={() => s.moveLayer(1)} disabled={active >= layers.length - 1} />
        <IconButton icon="arrowDown" title="Move layer down" size={13} onClick={() => s.moveLayer(-1)} disabled={active === 0} />
        <IconButton icon="layers" title="Merge down" size={13} onClick={s.mergeDown} disabled={active === 0} />
        <IconButton icon="trash" title="Delete layer" size={13} onClick={s.deleteLayer} disabled={layers.length <= 1} />
      </div>
      <ul className="pixel-layers">
        {layers
          .map((l, i) => ({ l, i }))
          .reverse()
          .map(({ l, i }) => (
            <li
              key={i}
              className={`pixel-layer${i === active ? ' is-active' : ''}`}
              onClick={() => s.setLayer(i)}
              onDoubleClick={() => {
                void promptText('Layer name', l.name).then((name) => {
                  if (name?.trim()) s.setLayerProps(i, { name: name.trim() });
                });
              }}
            >
              <button
                type="button"
                className="ibtn"
                title={l.visible ? 'Hide layer' : 'Show layer'}
                onClick={(e) => {
                  e.stopPropagation();
                  s.setLayerProps(i, { visible: !l.visible });
                }}
              >
                <Icon name={l.visible ? 'eye' : 'eyeOff'} size={13} />
              </button>
              <span className="pixel-layer-name">{l.name}</span>
              {l.opacity < 1 && <span className="muted">{Math.round(l.opacity * 100)}%</span>}
            </li>
          ))}
      </ul>
      {current && (
        <label className="pixel-row">
          <span className="muted">Opacity</span>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(current.opacity * 100)}
            onChange={(e) => s.setLayerProps(active, { opacity: Number(e.target.value) / 100 })}
          />
        </label>
      )}
    </section>
  );
}

function CanvasPanel() {
  const doc = usePixel((s) => s.doc);
  const s = usePixel.getState();
  if (!doc) return null;
  const pivot = doc.pivot;
  return (
    <section className="pixel-section">
      <div className="pixel-section-head">Canvas</div>
      <div className="pixel-row">
        <span className="muted">Size</span>
        <span className="mono">
          {doc.width}×{doc.height}
        </span>
        <span className="bar-spacer" />
        <button
          type="button"
          className="ghost small"
          onClick={() => {
            void promptText('New canvas size (WxH, up to 4096x4096). Art stays anchored at the bottom center.', `${doc.width}x${doc.height}`).then((v) => {
              const m = v?.match(/^\s*(\d+)\s*[x×, ]\s*(\d+)\s*$/i);
              if (m) s.resize(Math.max(1, Math.min(4096, Number(m[1]))), Math.max(1, Math.min(4096, Number(m[2]))), 'bottom');
            });
          }}
        >
          Resize…
        </button>
      </div>
      <div className="pixel-row" title="How this art looks when used as a model texture">
        <span className="muted">On models</span>
        <select
          value={doc.filter ?? 'auto'}
          onChange={(e) => s.setFilter(e.target.value === 'auto' ? undefined : (e.target.value as 'nearest' | 'linear'))}
        >
          <option value="auto">Auto ({doc.width <= 256 && doc.height <= 256 ? 'pixelated' : 'smooth'})</option>
          <option value="nearest">Pixelated</option>
          <option value="linear">Smooth</option>
        </select>
      </div>
      <div className="pixel-row" title="Where the sprite's position sits on the art (0..1 from the left / bottom)">
        <span className="muted">Pivot</span>
        <select
          value={!pivot ? 'center' : pivot[0] === 0.5 && pivot[1] === 0 ? 'feet' : pivot[0] === 0.5 && pivot[1] === 0.5 ? 'center' : 'custom'}
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'center') s.setPivot(undefined);
            else if (v === 'feet') s.setPivot([0.5, 0]);
            else s.setPivot(pivot ?? [0.5, 0.5]);
          }}
        >
          <option value="center">Center</option>
          <option value="feet">Bottom center (feet)</option>
          <option value="custom">Custom</option>
        </select>
      </div>
      {pivot && !(pivot[0] === 0.5 && pivot[1] === 0) && !(pivot[0] === 0.5 && pivot[1] === 0.5) && (
        <div className="pixel-row">
          <input type="number" step={0.05} value={pivot[0]} onChange={(e) => s.setPivot([Number(e.target.value), pivot[1]])} aria-label="Pivot X" />
          <input type="number" step={0.05} value={pivot[1]} onChange={(e) => s.setPivot([pivot[0], Number(e.target.value)])} aria-label="Pivot Y" />
        </div>
      )}
    </section>
  );
}

export function PixelSide() {
  return (
    <div className="pixel-side">
      <ColorPanel />
      <PalettePanel />
      <LayersPanel />
      <CanvasPanel />
    </div>
  );
}
